import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  APP_AUTH_UNAUTHORIZED_EVENT,
  signIn,
  signOut,
  fetchWithTimeout,
  recoverPoisonedSsoSession,
  postLoginReturnTarget,
} from './appAuth';
import { refreshIdentity, useIdentity } from './sessionIdentity';
import { backendBaseUrl } from '../config';
import { cancelBootWatchdog } from '../boot/bootWatchdog';

interface AppAuthGateProps {
  children: React.ReactNode;
}

// Verified anonymous identity enters SSO once. The return marker survives the full-page login
// without client storage; a still-anonymous return permits one poisoned-token recovery. An
// unavailable /api/me read cannot enter either path.
const SSO_RETRY_PARAM = 'sso_retry';

// TD-359: second one-shot marker for the poisoned-EasyAuth-token recovery. Flow:
//   bare anonymous            → redirect to SSO with ?sso_retry=1
//   back, STILL anonymous     → the SWA token store is poisoned (a stale pre-offline_access session
//                               replaying a dead token); a plain re-login reuses it, so instead
//                               recoverPoisonedSsoSession() does /.auth/logout → re-login with ?sso_recovered=1
//   back AGAIN, still anon    → eviction did not restore a verified session;
//                               terminate at the fail-loud SignInUnreachable screen — never loop.
// A URL marker (not storage) so it survives the full-page /.auth/logout round trip in private mode.
const SSO_RECOVERED_PARAM = 'sso_recovered';

// ── WI-19: SSO session exchange (ticket → httpOnly sso_session cookie) ───────
//
// At SWA login the rolesSource mints a short signed ticket into the forwarded
// principal's userRoles; POST /api/auth/session exchanges it SERVER-SIDE for the
// full signed assertion in an httpOnly first-party cookie. The FE never sees
// either value — it only fires the empty-body POST once per app load. On EITHER
// outcome (2xx = a session cookie now exists; 401 = no SSO identity, e.g. an
// anonymous visitor or a stale >5-min ticket), it then publishes the caller's
// identity from the PERSISTED read surface GET /api/me (persist-then-render —
// never the mint response, which deliberately echoes nothing) into the shared
// identity store (sessionIdentity).
// That published identity is what the gate below renders from (WI-SSO-UI) — the app
// shell is shown ONLY once /api/me confirms an authenticated session.
//
// SIGNED-OUT SUPPRESSION: on an explicit signed-out landing (?signedout=1) the
// exchange POST is NOT run — a still-live SWA ticket could otherwise silently
// RE-MINT sso_session and resurrect the session the user just ended. Identity is
// still published from /api/me so the signed-out landing can VERIFY the session is
// actually gone (persist-then-render) rather than trust the logout POST's 2xx.
//
// pristineFetch: captured at MODULE-EVALUATION time. ES-module imports evaluate
// before the importing module's body runs, so this executes during main.tsx's
// import phase — BEFORE main.tsx's body calls installAppAuthFetchInterceptor().
// The exchange must NOT ride the intercepted fetch, and the reason SURVIVES the
// R-Q15 magic-link removal (2026-08-29) unchanged: a 401 from this POST is the
// EXPECTED outcome for a caller with no SSO ticket, so riding the interceptor
// would fire APP_AUTH_UNAUTHORIZED_EVENT on every such app load — re-entering the
// gate's unauthorized handler (branch recompute + a second refreshIdentity) from
// inside the very bootstrap that is about to publish identity anyway. The old
// wording gave the consequence as "churn a magic-link customer's grant marker";
// that marker is deleted, the self-triggering 401 is not.
// refreshIdentity is handed the same pristine fetch.
const pristineFetch = window.fetch.bind(window);

// One-shot per app LOAD (module-scoped so gate remounts / StrictMode double-
// invocation can never re-fire the POST).
let ssoExchangeFired = false;

async function exchangeSsoSession(): Promise<void> {
  if (ssoExchangeFired) return;
  ssoExchangeFired = true;
  // Sticky sign-out: do not exchange on the signed-out landing (no silent re-mint), but still publish
  // identity so the landing can verify the session is gone and the store resolves out of loading.
  if (readUrlSearch().get('signedout') === '1') {
    await refreshIdentity(pristineFetch);
    return;
  }
  try {
    // Bounded (skeptical-verify #1): a HUNG exchange must not wedge the gate forever. The app now
    // renders ONLY after identity resolves, so an unbounded POST that never settles (TCP open, no
    // response) would pin every visitor on the splash. On timeout the abort throws → the catch runs
    // refreshIdentity (itself bounded 8s) so identity ALWAYS resolves.
    const resp = await fetchWithTimeout(`${backendBaseUrl}/api/auth/session`, {
      method: 'POST',
      // Required for the Set-Cookie response to be honored on the cross-origin
      // SWA (FE) → Container Apps (API) path.
      credentials: 'include',
    }, 5000, pristineFetch);
    // N-3 follow-on (2026-09-06): the STATUS was discarded entirely, so a 500 from the mint, a 503
    // from a cold API, or a CSRF 403 were all indistinguishable from the ONE status that is
    // expected here — and every one of them left the caller silently un-minted with nothing in the
    // console. 401 stays quiet BY DESIGN (see the block above: a caller with no SSO ticket, or a
    // reload re-presenting the consumed single-use one, is the normal steady state and firing the
    // unauthorized handler here would re-enter the bootstrap). Everything else is a real failure of
    // a trust-surface call and now says so. It still must not block the gate — no SSO session is a
    // renderable state — so this reports and falls through to the identity publish exactly as before.
    if (!resp.ok && resp.status !== 401) {
      console.error(
        `[AppAuthGate] WI-19 SSO session exchange failed: HTTP ${resp.status}. No sso_session was ` +
        `minted; the app renders from whatever /api/me reports.`);
    }
    // Whether the exchange minted an sso_session (2xx) or the caller has no SSO
    // identity (401), publish identity from the persisted /api/me AFTER the
    // exchange settles so the gate renders signed-in state without racing the
    // cookie mint. The signedout/bypass cascade below reads that identity.
    await refreshIdentity(pristineFetch);
  } catch (err) {
    // W7 convention: network failures surface via console, never silently
    // swallowed — and never block the gate (no SSO session is a valid state).
    // Still publish identity so the gate leaves its loading state.
    console.warn('[AppAuthGate] WI-19 SSO session exchange failed (network/timeout):', err);
    await refreshIdentity(pristineFetch);
  }
}

function readUrlSearch(): URLSearchParams {
  try {
    return new URLSearchParams(window.location.search);
  } catch (err) {
    console.warn('[AppAuthGate] readUrlSearch failed:', err);
    return new URLSearchParams();
  }
}

// WI-SSO-UI: the gate is an identity-aware state machine (first-match-wins). The URL-derived branch
// comes from computeBranch(); the identity-derived branches (splash / render-app / auto-redirect) are
// decided in the component from the published /api/me identity, so the app shell is NEVER shown to an
// unauthenticated visitor (an anonymous visitor is redirected to SSO; a signed-out one stays put):
//   - signedout : the sticky signed-out landing (set by signOut) — verifies /api/me then shows a
//                 Sign-in button (or a fail-loud "sign-out didn't complete" if the session is still live).
//   - bypass    : identity-gated — loading → splash; authenticated (a verified SSO session) → render
//                 the app; resolved-anonymous → auto-redirect to SSO (target step 1).
//
// ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15 (FULL REMOVAL of the magic-link plane). Two further
// branches stood here and are FULL-DELETED with the plane they served: `redeem` (a `#access=<token>`
// magic-link token in the URL → redeem it, which won first) and `expired` (the `?view=access-expired`
// terminal page shown when a magic-link cookie could not authorize). Neither URL shape is produced by
// anything any more, so both now fall through to `bypass` and are handled as what they are: an
// unrecognised URL on an ordinary visit.
type Phase5Branch =
  | { kind: 'signedout' }
  | { kind: 'bypass' };

function computeBranch(): Phase5Branch {
  const params = readUrlSearch();
  // Sticky signed-out landing (set by signOut's post_logout_redirect_uri=/?signedout=1). Distinguishes a
  // deliberate sign-out from a bare visit so the gate does NOT auto-redirect back into Entra (which would
  // silently re-auth against the still-live federated session). Self-clears on the next bare '/'.
  if (params.get('signedout') === '1') {
    return { kind: 'signedout' };
  }
  return { kind: 'bypass' };
}

// The loop guard is a one-time marker carried in the post-login return URL (storage-independent).
function readRedirectAttempted(): boolean {
  return readUrlSearch().get(SSO_RETRY_PARAM) === '1';
}

// TD-359: has the poisoned-token eviction (/.auth/logout → re-login) already been attempted this cycle?
// When true, another verified-anonymous return terminates at SignInUnreachable instead of
// repeating recovery. An unavailable identity read takes its own retry screen.
function readRecoveryAttempted(): boolean {
  return readUrlSearch().get(SSO_RECOVERED_PARAM) === '1';
}

// ── minimal, dependency-light screens for the non-app gate states ────────────
// Deliberately plain (no MUI): AppAuthGate wraps the app, so the theme provider is inside `children`
// and is not mounted for these gate-level screens. i18n IS available: main.tsx imports `../i18n`
// (module-scope i18next init) before the first render, so react-i18next's default instance resolves
// here without a provider — the TD-333 bootstrap strings below are translated (en/fr/zh-CN).
function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#1f2937',
      }}
    >
      <div style={{ maxWidth: 420 }}>{children}</div>
    </div>
  );
}

const actionButtonStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#fff',
  background: '#2563eb',
  border: 'none',
  borderRadius: 6,
  padding: '10px 20px',
  cursor: 'pointer',
};

// Neutral holding screen while identity resolves / a redirect is in flight. NOT the app shell — an
// unauthenticated visitor must never see the (empty, access-less) project list.
function AuthSplash() {
  return (
    <CenteredMessage>
      <p style={{ fontSize: 15, color: '#6b7280' }}>Loading…</p>
    </CenteredMessage>
  );
}

// TD-333: the ?sso_retry=1 return leg of the silent SSO refresh previously held the bare
// "Loading…" splash for the whole /.auth/complete → POST /api/auth/session → GET /api/me
// round-trip — measured live as an effectively BLANK page users read as an outage. While
// that bootstrap is in flight, say what is actually happening.
function SsoBootstrapSplash() {
  const { t } = useTranslation('common');
  return (
    <CenteredMessage>
      <p style={{ fontSize: 15, color: '#6b7280' }}>{t('appAuth.ssoBootstrap.signingBackIn')}</p>
    </CenteredMessage>
  );
}

// TD-333 timeout fallback: the bootstrap's own fetches are bounded (exchange 5s + /api/me 8s),
// but frontend-rules requires the SCREEN itself to carry a timeout fallback — never an
// indefinite holding state. Actionable error, explicit retry (a plain sign-in with no retry
// marker → a fresh attempt), no automatic re-redirect (no loop).
function SsoBootstrapStuck() {
  const { t } = useTranslation('common');
  return (
    <CenteredMessage>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>{t('appAuth.ssoBootstrap.stuckTitle')}</h1>
      <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px' }}>{t('appAuth.ssoBootstrap.stuckBody')}</p>
      <button type="button" onClick={() => signIn()} style={actionButtonStyle}>
        {t('appAuth.ssoBootstrap.tryAgain')}
      </button>
    </CenteredMessage>
  );
}

// TD-333: worst-case healthy bootstrap on the retry leg is bounded by the exchange POST (5s)
// + the /api/me read (8s) ≈ 13s; 20s adds margin so the fallback can never race a healthy
// resolve. If identity is still unresolved past this, something is genuinely wedged (a hung
// body read, a dropped publish) — surface the actionable error instead of holding the splash.
const SSO_BOOTSTRAP_TIMEOUT_MS = 20_000;

// A failed identity read says nothing about the user's session. Retry only the persisted read;
// never evict cookies or enter SSO to compensate for an unavailable API.
function IdentityUnavailable() {
  const { t } = useTranslation('common');
  return (
    <CenteredMessage>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>{t('appAuth.identityUnavailable.title')}</h1>
      <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px' }}>{t('appAuth.identityUnavailable.body')}</p>
      <button type="button" onClick={() => { void refreshIdentity(pristineFetch); }} style={actionButtonStyle}>
        {t('appAuth.identityUnavailable.retry')}
      </button>
    </CenteredMessage>
  );
}

// Sticky signed-out landing: a single Sign-in action, NO auto-redirect (so a manual sign-out stays put).
function SignedOutLanding() {
  return (
    <CenteredMessage>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>You&rsquo;re signed out</h1>
      <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px' }}>You have been signed out of Bennie.</p>
      <button type="button" onClick={() => signIn()} style={actionButtonStyle}>
        Sign in
      </button>
    </CenteredMessage>
  );
}

// Fail-loud: the sign-out landing read /api/me and the session is STILL live — the server-side cookie
// clear did not take (e.g. its Set-Cookie was stripped in transit). Do NOT present a false "signed out"
// screen; surface it and offer a retry that re-runs the full sign-out.
function SignOutIncomplete() {
  return (
    <CenteredMessage>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>Sign-out didn&rsquo;t complete</h1>
      <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px' }}>
        Your session is still active. Please try signing out again.
      </p>
      <button type="button" onClick={() => { void signOut(); }} style={actionButtonStyle}>
        Sign out
      </button>
    </CenteredMessage>
  );
}

// Fail-loud terminal: the sign-in round-trip returned a verified anonymous identity (for example, the
// login was cancelled). We do NOT redirect again — that would loop through Entra. Offer an explicit retry
// (a plain sign-in with no retry marker → a fresh attempt).
function SignInUnreachable() {
  return (
    <CenteredMessage>
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>Sign-in is unavailable</h1>
      <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 20px' }}>
        We couldn&rsquo;t complete sign-in. Please try again in a moment.
      </p>
      <button type="button" onClick={() => signIn()} style={actionButtonStyle}>
        Try again
      </button>
    </CenteredMessage>
  );
}

export function AppAuthGate({ children }: AppAuthGateProps) {
  // The gate renders from the PUBLISHED /api/me identity (persist-then-render) — never optimistic state.
  const identity = useIdentity();
  // The branch is REACTIVE to APP_AUTH_UNAUTHORIZED_EVENT (see effect below): a bare 401
  // recomputes the branch and reconciles identity.
  const [phase5Branch, setPhase5Branch] = React.useState<Phase5Branch>(computeBranch);

  // TD-762: React has MOUNTED. Disarm the index.html boot watchdog — armed pre-React precisely so
  // it can still fire when the bundle itself never arrives (measured on the PROD certification walk:
  // readyState "loading" for 40+s, 2 assets completed, no bundle, blank page, no error, no retry).
  // This gate is the earliest point at which "React committed and painted something" is true for
  // EVERY branch — splash, signed-out and app shell all render from here — so the signal is
  // MOUNT, not auth success, and a slow-but-successful boot cancels rather than trips it. Idempotent
  // under StrictMode's double-invoked effects.
  React.useEffect(() => {
    cancelBootWatchdog();
  }, []);

  React.useEffect(() => {
    const handler = () => {
      // A 401 from any backend call — the SSO session died (e.g. the ~8h TTL elapsed). The handler does
      // exactly two things, and NOTHING else: recompute the URL-derived branch, and re-read identity from
      // the PERSISTED backend surface GET /api/me. It never decides authentication itself — that would be
      // rendering from a response payload rather than from persisted state. The reconcile is what makes
      // the change visible: with `authenticated` now false, the bypass branch's auto-redirect drives the
      // caller to sign-on on their next action (target step 4c).
      //
      // ⚠️ AMENDED 2026-08-29 — operator ruling R-Q15. The whole stated rationale here was a NEGATIVE
      // one: that we "deliberately do NOT clear the grant marker", because a bare 401 reaching a
      // magic-link customer meant their `access_session` was still live (a dead one yielded an ACCESS_*
      // code routed to the access-expired page by the access interceptor, not a bare 401), so clearing
      // it would strand a valid customer at an SSO login they could not complete. Every noun in that
      // sentence is now deleted — the marker, the customer plane, the interceptor and the ACCESS_* codes
      // — so the caveat is VOID rather than merely stale. There is also no longer an "access-error 401"
      // to distinguish a bare 401 from: on the SSO-only plane every 401 is this one.
      setPhase5Branch(computeBranch());
      void refreshIdentity(pristineFetch);
    };
    window.addEventListener(APP_AUTH_UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(APP_AUTH_UNAUTHORIZED_EVENT, handler);
  }, []);

  // WI-19: fire the one-shot SSO session exchange on gate mount (suppressed on the signed-out landing).
  // It publishes the /api/me identity the gate renders from.
  React.useEffect(() => {
    void exchangeSsoSession();
  }, []);

  // TD-333: wall-clock fallback for the ?sso_retry=1 bootstrap window. Armed only while the
  // gate is holding the bootstrap splash (bypass branch, identity unresolved, retry marker
  // present); disarmed the moment identity resolves (the other branches then own the screen).
  const [bootstrapTimedOut, setBootstrapTimedOut] = React.useState(false);
  const inSsoBootstrap = phase5Branch.kind === 'bypass' && identity.loading && readRedirectAttempted();
  React.useEffect(() => {
    if (!inSsoBootstrap) return;
    const timer = setTimeout(() => setBootstrapTimedOut(true), SSO_BOOTSTRAP_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [inSsoBootstrap]);

  // WI-SSO-UI target step 1 + 4c: auto-redirect a RESOLVED, bare-anonymous visitor to SSO. Only from the
  // 'bypass' branch (the 'signedout' landing renders its own screen); only AFTER identity resolves, so a
  // still-loading session is never redirected out from under an in-flight /api/me; guarded against a
  // redirect loop by the URL retry marker on a verified anonymous return.
  React.useEffect(() => {
    if (phase5Branch.kind !== 'bypass') return;
    if (identity.loading || identity.unavailable) return;
    if (identity.authenticated) {
      // Signed in on the verified SSO session. If we arrived via the auto-redirect, strip the
      // one-time markers from the URL so they cannot wrongly trip the loop guard on a later session.
      // F-10: delete ONLY those two params — rewriting to a bare pathname also erased the deep link
      // the redirect above now preserves, so the address bar (and a refresh) lost the destination the
      // user had just been returned to. The fragment is not re-emitted: this writes `pathname + search`
      // only, which is the same same-origin discipline `postLoginReturnTarget` applies.
      if (readRedirectAttempted()) {
        try {
          const cleaned = new URL(window.location.href);
          cleaned.searchParams.delete(SSO_RETRY_PARAM);
          cleaned.searchParams.delete(SSO_RECOVERED_PARAM);
          window.history.replaceState(null, '', `${cleaned.pathname}${cleaned.search}`);
        } catch (err) {
          console.warn('[AppAuthGate] could not strip the one-time SSO markers from the URL:', err);
        }
      }
      return;
    }
    // Resolved + not authenticated.
    if (readRedirectAttempted()) {
      // Came back from the SSO round-trip STILL anonymous.
      // TD-359: the usual cause is a POISONED EasyAuth token store (a pre-offline_access session
      // replaying a dead access token) — a plain re-login reuses it, so ONE automatic eviction
      // (/.auth/logout → re-login) self-heals it. Do it exactly once, guarded by sso_recovered so a
      // verified anonymous return still terminates at SignInUnreachable rather than looping.
      if (!readRecoveryAttempted()) {
        recoverPoisonedSsoSession();
      }
      return; // recovery navigates away; if already recovered, fall through to the fail-loud screen
    }
    // Bare anonymous (no marker). Redirect to SSO carrying the retry marker in the post-login return
    // URL — survives the full-page round trip with NO client storage, so a verified anonymous return cannot
    // produce an infinite Entra loop. F-10: the marker is ADDED to the visitor's CURRENT location
    // (postLoginReturnTarget — same-origin path + query only, open-redirect-guarded) instead of
    // replacing it with a bare '/', so an anonymous deep link (?view=toolbox, ?workspaceId=…) is not
    // discarded by the login bounce and lands where the user actually asked to go.
    window.location.assign(
      `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(
        postLoginReturnTarget({ [SSO_RETRY_PARAM]: '1' }),
      )}`,
    );
  }, [phase5Branch, identity.loading, identity.unavailable, identity.authenticated]);

  if (identity.unavailable) return <IdentityUnavailable />;

  if (phase5Branch.kind === 'signedout') {
    // Persist-then-render the sign-out CLAIM (skeptical-verify #2 / code-truth #3): the exchange POST is
    // suppressed on this landing but /api/me WAS read, so verify the session is actually gone rather than
    // trusting the logout POST's 2xx (whose clearing Set-Cookie could have been stripped in transit). If
    // /api/me still reports authenticated, the clear did NOT take — surface it, do not present a lie.
    if (identity.loading) return <AuthSplash />;
    if (identity.authenticated) return <SignOutIncomplete />;
    return <SignedOutLanding />;
  }

  // 'bypass' — identity-gated. The app shell renders ONLY from a fresh, verified /api/me
  // (persist-then-render); an unauthenticated visitor is either still resolving (splash) or being
  // redirected to SSO, and never sees the shell (closes the D1 empty-shell landing + the D3 render path).
  if (identity.loading) {
    // TD-333: on the ?sso_retry=1 return leg, name the work ("signing you back in…") instead of
    // the anonymous splash, and fall back to an actionable error if the bootstrap wedges past
    // its wall-clock budget — never an indefinite blank retry window.
    if (readRedirectAttempted()) {
      return bootstrapTimedOut ? <SsoBootstrapStuck /> : <SsoBootstrapSplash />;
    }
    return <AuthSplash />;
  }
  if (identity.authenticated) {
    return <>{children}</>;
  }
  if (readRedirectAttempted()) {
    // TD-359: still anonymous after the SSO round-trip. If eviction has NOT yet been tried, the effect
    // above is navigating to /.auth/logout → re-login to clear a poisoned token store — hold the
    // "signing you back in…" splash (or the wall-clock-bounded stuck screen), NOT a false error. Only
    // after eviction has ALSO failed (sso_recovered) is this a genuine dead-end.
    if (!readRecoveryAttempted()) {
      return bootstrapTimedOut ? <SsoBootstrapStuck /> : <SsoBootstrapSplash />;
    }
    return <SignInUnreachable />;
  }
  // Resolved-anonymous: the effect above is firing the SSO redirect; hold on a neutral splash meanwhile.
  return <AuthSplash />;
}
